import { isArray, isNumber, isObject, isString } from 'lodash';
import { Event, ScoredEvent } from '@/types/event';
import { formatDate } from '@/utils/date';

export enum DateTimeMode {
    ALL = 'all',
    TODAY = 'today',
    YESTERDAY = 'yesterday',
    LAST_HOUR = 'last-hour',
    LAST_3_HOURS = 'last-3-hours',
    LAST_6_HOURS = 'last-6-hours',
    LAST_12_HOURS = 'last-12-hours',
    LAST_24_HOURS = 'last-24-hours',
    LAST_3_DAYS = 'last-3-days',
    LAST_7_DAYS = 'last-7-days',
    LAST_14_DAYS = 'last-14-days',
    LAST_30_DAYS = 'last-30-days',
    CUSTOM = 'custom',
}

export enum SortOption {
    DATE_NEWEST = 'date-newest',
    DATE_OLDEST = 'date-oldest',
    VIDEO_TIME_NEWEST = 'video-time-newest',
    VIDEO_TIME_OLDEST = 'video-time-oldest',
    CONFIDENCE = 'confidence',
    SOURCE_A_Z = 'source-a-z',
    SOURCE_Z_A = 'source-z-a',
}

export interface DateTimeFilter {
    mode: DateTimeMode;
    customRange?: {
        startDate: Date;
        endDate: Date;
    };
}

export interface ConfidenceFilter {
    enabled: boolean;
    min: number;
    max: number;
}

export interface QueryFilters {
    dateTime: DateTimeFilter;
    confidence: ConfidenceFilter;
    // When true, return only scene (whole-frame) events.
    sceneOnly: boolean;
}

export const DEFAULT_CONFIDENCE_THRESHOLD = 40;

export function getDateRangeFromMode(
    mode: DateTimeMode,
): { startDate: Date; endDate: Date } | null {
    const now = new Date();
    const endDate = new Date(now);
    let startDate = new Date(now);

    switch (mode) {
        case DateTimeMode.TODAY:
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
        case DateTimeMode.YESTERDAY:
            startDate.setDate(startDate.getDate() - 1);
            startDate.setHours(0, 0, 0, 0);
            endDate.setDate(endDate.getDate() - 1);
            endDate.setHours(23, 59, 59, 999);
            break;
        case DateTimeMode.LAST_HOUR:
            startDate.setHours(startDate.getHours() - 1);
            break;
        case DateTimeMode.LAST_3_HOURS:
            startDate.setHours(startDate.getHours() - 3);
            break;
        case DateTimeMode.LAST_6_HOURS:
            startDate.setHours(startDate.getHours() - 6);
            break;
        case DateTimeMode.LAST_12_HOURS:
            startDate.setHours(startDate.getHours() - 12);
            break;
        case DateTimeMode.LAST_24_HOURS:
            startDate.setDate(startDate.getDate() - 1);
            break;
        case DateTimeMode.LAST_3_DAYS:
            startDate.setDate(startDate.getDate() - 3);
            startDate.setHours(0, 0, 0, 0);
            break;
        case DateTimeMode.LAST_7_DAYS:
            startDate.setDate(startDate.getDate() - 7);
            startDate.setHours(0, 0, 0, 0);
            break;
        case DateTimeMode.LAST_14_DAYS:
            startDate.setDate(startDate.getDate() - 14);
            startDate.setHours(0, 0, 0, 0);
            break;
        case DateTimeMode.LAST_30_DAYS:
            startDate.setDate(startDate.getDate() - 30);
            startDate.setHours(0, 0, 0, 0);
            break;
        case DateTimeMode.ALL:
            return null;
        default:
            return null;
    }

    return { startDate, endDate };
}

export function getDateRangeFromFilter(
    dateTime: DateTimeFilter,
): { startDate: Date; endDate: Date } | null {
    if (dateTime.mode === DateTimeMode.CUSTOM && dateTime.customRange) {
        return dateTime.customRange;
    }
    return getDateRangeFromMode(dateTime.mode);
}

export function applyFiltersToEvents(
    events: ScoredEvent[],
    filters: QueryFilters,
): ScoredEvent[] {
    let filtered = [...events];

    if (filters.confidence.enabled) {
        const minConfidence = filters.confidence.min;
        const maxConfidence = filters.confidence.max;
        filtered = filtered.filter((scoredEvent) => {
            return (
                scoredEvent.confidence >= minConfidence &&
                scoredEvent.confidence <= maxConfidence
            );
        });
    }

    const dateRange =
        filters.dateTime.mode === DateTimeMode.CUSTOM &&
        filters.dateTime.customRange
            ? filters.dateTime.customRange
            : getDateRangeFromMode(filters.dateTime.mode);

    if (dateRange) {
        const startTime = dateRange.startDate.getTime() / 1000;
        const endTime = dateRange.endDate.getTime() / 1000;

        filtered = filtered.filter((scoredEvent) => {
            return (
                scoredEvent.event.createdAt >= startTime &&
                scoredEvent.event.createdAt <= endTime
            );
        });
    }

    return filtered;
}

function compareEvents(
    a: ScoredEvent,
    b: ScoredEvent,
    sortOption: SortOption,
): number {
    switch (sortOption) {
        case SortOption.DATE_NEWEST:
            return b.event.createdAt - a.event.createdAt;
        case SortOption.DATE_OLDEST:
            return a.event.createdAt - b.event.createdAt;
        case SortOption.CONFIDENCE:
            return b.confidence - a.confidence;
        case SortOption.SOURCE_A_Z: {
            const nameA = a.event.sourceName ?? a.event.sourceId;
            const nameB = b.event.sourceName ?? b.event.sourceId;
            return nameA.localeCompare(nameB);
        }
        case SortOption.SOURCE_Z_A: {
            const nameA = a.event.sourceName ?? a.event.sourceId;
            const nameB = b.event.sourceName ?? b.event.sourceId;
            return nameB.localeCompare(nameA);
        }
        default:
            return 0;
    }
}

export function sortEvents(
    events: ScoredEvent[],
    sortOption: SortOption,
    pageMap?: Map<string, number>,
): ScoredEvent[] {
    const sorted = [...events];

    sorted.sort((a, b) => {
        if (pageMap) {
            const pageA = pageMap.get(a.event.id) ?? 0;
            const pageB = pageMap.get(b.event.id) ?? 0;
            if (pageA !== pageB) {
                return pageA - pageB;
            }
        }
        return compareEvents(a, b, sortOption);
    });

    return sorted;
}

export function getDateTimeLabel(dateTime: DateTimeFilter): string {
    switch (dateTime.mode) {
        case DateTimeMode.TODAY:
            return 'Today';
        case DateTimeMode.YESTERDAY:
            return 'Yesterday';
        case DateTimeMode.LAST_HOUR:
            return 'Last hour';
        case DateTimeMode.LAST_3_HOURS:
            return 'Last 3 hours';
        case DateTimeMode.LAST_6_HOURS:
            return 'Last 6 hours';
        case DateTimeMode.LAST_12_HOURS:
            return 'Last 12 hours';
        case DateTimeMode.LAST_24_HOURS:
            return 'Last 24 hours';
        case DateTimeMode.LAST_3_DAYS:
            return 'Last 3 days';
        case DateTimeMode.LAST_7_DAYS:
            return 'Last 7 days';
        case DateTimeMode.LAST_14_DAYS:
            return 'Last 14 days';
        case DateTimeMode.LAST_30_DAYS:
            return 'Last 30 days';
        case DateTimeMode.CUSTOM:
            if (dateTime.customRange) {
                const start = formatDate(dateTime.customRange.startDate);
                const end = formatDate(dateTime.customRange.endDate);
                return `${start} - ${end}`;
            }
            return 'Custom range';
        case DateTimeMode.ALL:
        default:
            return 'All time';
    }
}

export function isValidEvent(obj: any): obj is Event {
    if (!isObject(obj)) return false;

    const event = obj as Event;
    if (
        !isString(event.id) ||
        !isString(event.sourceId) ||
        !isNumber(event.createdAt) ||
        !isArray(event.frames)
    ) {
        return false;
    }

    // Scene events have no per-frame boxes (thumbnail + snapshot only).
    return event.type === 'SCENE' || event.frames.length > 0;
}
